import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextField } from '@speeki/global-ui-components';
import { Typography } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { FieldWrapper } from '@components/atoms/FieldWrapper/FieldWrapper';

import { useOrganisationLabel } from '@utils/hooks/useOrganisationLabel';

import {
    InfoBoldedText,
    InformationWrapper,
    Input,
    OptionalText,
    OptionalTextWrapper,
    PersonalInfoButton,
    PersonalInfoFormWrapper,
} from '@domains/new-conversation/organisms/ConversationDetailsForm/PersonalInformationForm.styled';
import { ReporterEmailConfirmation } from '@domains/new-conversation/organisms/ReporterEmailConfirmation/ReporterEmailConfirmation';
import { AttachedField } from '@domains/new-conversation/organisms/ReporterEmailConfirmation/ReporterEmailConfirmation.styled';
import { DetailsSchemaForm } from '@domains/new-conversation/useConversationForm';

interface PersonalInformationFormProps {
    isShown: boolean;
    mandatory: boolean;
    showForm: (data: boolean) => void;
}

export const PersonalInformationForm = ({
    isShown,
    mandatory,
    showForm,
}: PersonalInformationFormProps) => {
    const {
        formState: { errors },
        register,
        setFocus,
        setValue,
        watch,
    } = useFormContext<DetailsSchemaForm>();
    const { t } = useTranslation();
    const organisationLabel = useOrganisationLabel();

    const email = watch('email');
    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const country = watch('country');

    const clearPersonalInformation = () => {
        for (const field of ['email', 'firstName', 'lastName'] as const) {
            setValue(field, undefined, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    useEffect(() => {
        if (isShown && !mandatory) {
            if (!email) {
                setValue('email', undefined, {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }
            if (!firstName) {
                setValue('firstName', undefined, {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }
            if (!lastName) {
                setValue('lastName', undefined, {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }
        }
    }, [isShown, mandatory, email, firstName, lastName]);

    return (
        <InformationWrapper>
            <OptionalTextWrapper>
                <IconManager icon={'SvgAlert'} size={14} />
                {!mandatory && (
                    <OptionalText>{t('OPTIONAL').toUpperCase()}</OptionalText>
                )}
            </OptionalTextWrapper>
            <Typography.TextBody_14_24>
                {mandatory ? (
                    <>
                        {t('NEW_CONVERSATION_MANDATORY_PERSONAL_INFO_TEXT')}{' '}
                        {country?.label}
                        {', '}
                        {t(
                            'NEW_CONVERSATION_MANDATORY_PERSONAL_INFO_TEXT_1',
                        )}{' '}
                        <InfoBoldedText>
                            {t(
                                'NEW_CONVERSATION_MANDATORY_PERSONAL_INFO_TEXT_2',
                            )}
                        </InfoBoldedText>{' '}
                        {t('NEW_CONVERSATION_MANDATORY_PERSONAL_INFO_TEXT_3')}
                    </>
                ) : (
                    t('NEW_CONVERSATION_PERSONAL_INFO_TEXT')
                )}
            </Typography.TextBody_14_24>
            {!isShown && !mandatory && (
                <PersonalInfoButton
                    onClick={() => {
                        showForm(true);
                    }}
                    size={'small'}
                    testId="button_new-conversation"
                    variant={'secondary'}
                >
                    {t('NEW_CONVERSATION_PERSONAL_INFO_BUTTON')}
                </PersonalInfoButton>
            )}
            {isShown && !mandatory && (
                <PersonalInfoButton
                    onClick={() => {
                        showForm(false);
                        /*
                         * Hiding the section is not enough: the report is built
                         * from the form values, not from whether they are on
                         * screen. Left behind, details the reporter just
                         * declined to give would still be submitted - and an
                         * unconfirmed address would hold up the report from a
                         * field they can no longer see.
                         */
                        clearPersonalInformation();
                    }}
                    size={'small'}
                    testId="button_new-conversation"
                    variant={'secondary'}
                >
                    {t('NEW_CONVERSATION_PERSONAL_INFO_BUTTON_NO')}
                </PersonalInfoButton>
            )}
            {(isShown || mandatory) && (
                <PersonalInfoFormWrapper>
                    <FieldWrapper>
                        <AttachedField>
                            <Input
                                {...register('email')}
                                autoComplete={'off'}
                                className="htmlToImageIgnore"
                                error={!!errors.email?.message}
                                label={
                                    mandatory
                                        ? `${t('REPORTER_IDENTITY_EMAIL_LABEL')}  *`
                                        : t(
                                              'REPORTER_IDENTITY_EMAIL_LABEL_OPTIONAL',
                                          )
                                }
                                // TODO: check what is wrong with that type
                                message={
                                    !!errors.email?.message
                                        ? t(errors.email?.message.toString())
                                        : mandatory
                                          ? t('EMAIL_ADDITIONAL')
                                          : t(
                                                'REPORTER_IDENTITY_EMAIL_DISCLOSURE',
                                                {
                                                    organisation:
                                                        organisationLabel,
                                                },
                                            )
                                }
                                placeholder={t('EMAIL_PLACEHOLDER')}
                                testId="lRNqyVn"
                                type={'email'}
                            />
                            {/*
                             * Confirmation runs in both modes (decided
                             * 2026-08-08: a mandatory address must be
                             * confirmed too). The removal exit is only offered
                             * while the address is optional - when the
                             * incident type forbids anonymous reporting the
                             * address is required by the form, so "remove it
                             * and continue without it" would promise a way out
                             * that does not exist.
                             */}
                            <ReporterEmailConfirmation
                                email={email}
                                isEmailValid={!errors.email?.message}
                                onChangeAddress={() => setFocus('email')}
                                onRemoveAddress={
                                    mandatory
                                        ? undefined
                                        : () =>
                                              setValue('email', undefined, {
                                                  shouldDirty: true,
                                                  shouldValidate: true,
                                              })
                                }
                            />
                        </AttachedField>
                    </FieldWrapper>
                    <FieldWrapper>
                        <Input
                            {...register('firstName')}
                            className="htmlToImageIgnore"
                            error={!!errors.firstName?.message}
                            label={`${t('FIRST_NAME')}  ${
                                mandatory ? '*' : ''
                            }`}
                            // TODO: check what is wrong with that type
                            message={
                                !!errors.firstName?.message
                                    ? t(errors.firstName?.message.toString())
                                    : undefined
                            }
                            placeholder={t('FIRST_NAME_PLACEHOLDER')}
                            testId="igwCfoV"
                        />
                    </FieldWrapper>
                    <FieldWrapper>
                        <TextField
                            {...register('lastName')}
                            className="htmlToImageIgnore"
                            error={!!errors.lastName?.message}
                            label={`${t('LAST_NAME')}  ${mandatory ? '*' : ''}`}
                            // TODO: check what is wrong with that type
                            message={
                                !!errors.lastName?.message
                                    ? t(errors.lastName?.message.toString())
                                    : undefined
                            }
                            placeholder={t('ENTER_LAST_NAME')}
                            testId="_HsWxvw"
                        />
                    </FieldWrapper>
                </PersonalInfoFormWrapper>
            )}
        </InformationWrapper>
    );
};
